import { IdName, CompanyResponse, CustomFieldDefinition, CustomFields } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';

// We're faking an entity, the following code maps between our representation and the backend representation

export interface CustomFieldResponse {
    id: string;
    tag: string;
    name: string;
    entityType: IdName;
    type: IdName;
    required: boolean;
    values: string;
}

export function getEntityTypes(admin: AdminEditorService, ownerId: string): Promise<IdName[]> {
    return admin.getCached(`listAssetTypes/${ownerId}`, api => api.entities.listAssetTypes(ownerId)).then(assetTypes => {
        const types = ['simcard', 'assettag', 'device', 'asset', 'event'];
        let usedAssetTypes = assetTypes.items;
        if (admin?.features?.page?.admin?.availableAssetTypes) {
            const shownItemIds = admin.features.page.admin.availableAssetTypes.split(',');
            usedAssetTypes = assetTypes.items.filter(item => shownItemIds.includes(item.id));
        }
        return [
            ...types.map(x => ({ id: x, name: admin.translate(`ENTITIES.${x.toUpperCase()}`) })),
            ...usedAssetTypes.map(x => ({ id: x.id, name: admin.i18n.instant(`SHARED.ASSET_TYPES.${x.tag.toUpperCase()}`) })).sort((a, b) => a.name.localeCompare(b.name)),
        ];
    });
}

export function getTypes(admin: AdminEditorService): IdName[] {
    return ['text', 'memo', 'combo', 'checkbox', 'date', 'datetime', 'url'].map(x => ({
        id: x,
        name: admin.translate(`EDITORS.CUSTOMFIELD.TYPES.${x.toUpperCase()}`),
    }));
}

export function getCustomFieldResponseFromCompany(admin: AdminEditorService, company: CompanyResponse, id: string): Promise<CustomFieldResponse> {
    const [ownerId, entityType, fieldId] = id.split('.');
    const field = company.customFields[entityType].find(x => x.id === fieldId);
    if (!field) {
        throw new NotFoundError(`Unable to find custom field with id "${fieldId}"`); // will be caught downstream
    }
    return Promise.all([
        getEntityTypes(admin, ownerId),
        getTypes(admin),
    ]).then(([entityTypes, types]) => {
        return CustomFieldDefinitionToCustomFieldResponse(entityTypes, types, ownerId, entityType, field);
    });
}

export class NotFoundError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'NotFoundError';
    }
}


export function CustomFieldDefinitionToCustomFieldResponse(entityTypes: IdName[], types: IdName[], ownerId: string, entityType: string, field: CustomFieldDefinition): CustomFieldResponse {
    return {
        id: `${ownerId}.${entityType}.${field.id}`,
        tag: field.id,
        name: field.title,
        required: field.required,
        entityType: entityTypes.find(x => x.id === entityType),
        type: types.find(x => x.id === field.type),
        values: (field.values || []).map(x => x.key).join('\n'),
    };

}

export function CustomFieldResopnseToCustomFieldDefinition(field: CustomFieldResponse): CustomFieldDefinition {
    const result = {
        id: field.tag,
        title: field.name,
        required: field.required,
        type: field.type && field.type.id,
        values: field.values && field.values.replace(/\r/g, '').split('\n').filter(x => x).map(x => ({ key: x, value: x })),
    };
    Object.keys(result).forEach(key => result[key] === undefined && delete result[key]); // strip undefined properties from the object
    return result;
}

export function UpdateCompanyCustomFields(admin: AdminEditorService, company: CompanyResponse, customFields: CustomFields): Promise<CompanyResponse> {
    return Promise.resolve().then(() => {
        switch (company.type) {
            case 'client': return admin.applyUpdate(api => api.accounts.updateClientDetails(company.id, { customFields: customFields }));
            case 'vendor': return admin.applyUpdate(api => api.accounts.updateVendorDetails(company.id, { customFields: customFields }));
            case 'distributor': return admin.applyUpdate(api => api.accounts.updateDistributorDetails(company.id, { customFields: customFields }));
            default: throw new Error('Unknown company type');
        }
    }).then(result => {
        return { ...company, customFields: result.customFields };
    });
}
